import { createFileRoute, Link } from "@tanstack/react-router";
import { concepts } from "@/content/en/concepts";
import { SectionHeading, BriefingCTA } from "@/components/en/EnPrimitives";
import { hreflangLinks } from "@/lib/seo";
import { pageTitle } from "@/lib/brand";

const CANONICAL = "https://smartsustainability.ai/concepts";
const TITLE = pageTitle("Concepts", "en");
const DESCRIPTION =
  "Original frameworks and definitions for understanding the age of artificial intelligence.";

export const Route = createFileRoute("/concepts/")({
  head: () => ({
    meta: [
      { title: TITLE },
      { name: "description", content: DESCRIPTION },
      { property: "og:title", content: TITLE },
      { property: "og:description", content: DESCRIPTION },
      { property: "og:url", content: CANONICAL },
      { property: "og:locale", content: "en_US" },
      { property: "og:locale:alternate", content: "tr_TR" },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "twitter:title", content: TITLE },
      { name: "twitter:description", content: DESCRIPTION },
    ],
    links: [
      { rel: "canonical", href: CANONICAL },
      ...hreflangLinks("/sozluk", "/concepts"),
    ],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "CollectionPage",
          name: TITLE,
          description: DESCRIPTION,
          url: CANONICAL,
          inLanguage: "en",
          isPartOf: {
            "@type": "WebSite",
            name: "Smart Sustainability",
            url: "https://smartsustainability.ai",
          },
          hasPart: concepts.map((c) => ({
            "@type": "DefinedTerm",
            name: c.name,
            description: c.oneLine,
            url: `${CANONICAL}/${c.slug}`,
          })),
        }),
      },
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "BreadcrumbList",
          itemListElement: [
            { "@type": "ListItem", position: 1, name: "Home", item: "https://smartsustainability.ai" },
            { "@type": "ListItem", position: 2, name: "Concepts", item: CANONICAL },
          ],
        }),
      },
    ],
  }),
  component: ConceptsIndex,
});


function ConceptsIndex() {
  return (
    <div className="mx-auto max-w-6xl px-6 py-16 md:py-20">
      <SectionHeading as="h1" kicker="Vocabulary" title="Concepts">
        Original frameworks and definitions for understanding the age of
        artificial intelligence. Each concept is a working tool, refined
        against evidence and use.
      </SectionHeading>

      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
        {concepts.map((c) => (
          <Link
            key={c.slug}
            to="/concepts/$slug"
            params={{ slug: c.slug }}
            className="group rounded-md border border-ink/10 bg-paper p-6 transition-colors hover:border-ink/40"
          >
            <p className="text-[10px] uppercase tracking-[0.22em] text-clay">Concept</p>
            <h2 className="mt-3 font-serif text-xl text-ink group-hover:text-clay transition-colors">
              {c.name}
            </h2>
            <p className="mt-3 text-[14px] leading-relaxed text-foreground/80">
              {c.oneLine}
            </p>
            <p className="mt-4 text-[10px] uppercase tracking-[0.18em] text-muted-foreground">
              {c.topics.join(" · ")}
            </p>
          </Link>
        ))}
      </div>

      <BriefingCTA />
    </div>
  );
}
